import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Film } from "lucide-react";

export default function VideoShowcase() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => setPlaying(false));
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section className="relative py-24 sm:py-28 overflow-hidden" data-testid="video-showcase-section">
      <div className="absolute inset-0 hero-grid opacity-60" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[34rem] h-[34rem] rounded-full bg-gold/10 blur-[130px]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">On the roof</p>
          <h2 className="font-display font-bold italic uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]" data-testid="video-heading">
            Watch the crew <span className="gold-text">in action.</span>
          </h2>
          <p className="mt-5 max-w-md text-base sm:text-lg text-slate-400 leading-relaxed">
            Real crews, real roofs, real results. This is how every DRC502 project looks from the top — clean tear-offs, precision installs and spotless finishes.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 border border-gold/25 bg-gold/5 rounded-full px-5 py-2.5" data-testid="video-badge">
            <Film size={15} className="text-gold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Filmed on a real DRC502 job site</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 2 }}
          whileInView={{ opacity: 1, y: 0, rotate: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-sm mx-auto w-full"
        >
          <div className="absolute -inset-6 bg-gold/15 blur-[70px] rounded-full" />
          <div className="relative rounded-3xl border border-gold/30 overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.6)] [clip-path:polygon(0_0,100%_0,100%_94%,92%_100%,0_100%)]" data-testid="video-frame">
            <video
              ref={videoRef}
              src="/hero-video.mp4"
              poster="/hero-bg.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full aspect-[9/16] object-cover"
              data-testid="showcase-video"
              onClick={toggle}
            />
            <button
              onClick={toggle}
              data-testid="video-toggle-button"
              aria-label={playing ? "Pause video" : "Play video"}
              className="absolute bottom-5 right-5 w-12 h-12 rounded-full bg-storm/70 backdrop-blur border border-gold/40 text-gold flex items-center justify-center hover:bg-gold hover:text-storm transition-colors"
            >
              {playing ? <Pause size={19} /> : <Play size={19} className="ml-0.5" />}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
